// Fritz Kamm AG · Mehrsprachigkeit
// HARD-OFF flag — wenn auf `false`, ist die Sprachumschaltung komplett unsichtbar.
// Site verhält sich identisch zur aktuellen rein-deutschen Version.
//
// Aktivieren:
//   1. ENABLE_I18N = true setzen (unten)
//   2. Komponenten via `useT()` an Übersetzungen anbinden (Anleitung in /app/memory/i18n.md)
//   3. Build neu deployen

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import translations from "./translations";

export const ENABLE_I18N = true;

// Aktive Sprachen: DE + FR + IT + EN (Schweizer Landessprachen + Englisch).
export const LANGUAGES = [
  { code: "de", label: "DE", name: "Deutsch", htmlLang: "de-CH" },
  { code: "fr", label: "FR", name: "Français", htmlLang: "fr-CH" },
  { code: "it", label: "IT", name: "Italiano", htmlLang: "it-CH" },
  { code: "en", label: "EN", name: "English", htmlLang: "en" },
];

const DEFAULT_LANG = "de";
const STORAGE_KEY = "fk_lang";
const AUTODETECT_KEY = "fk_lang_autodetected";

// Walk through navigator.languages preference list and return the first
// supported language code, or null if none matches.
function detectBrowserLang() {
  if (typeof navigator === "undefined") return null;
  const codes = LANGUAGES.map((l) => l.code);
  const list =
    Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language || ""];
  for (const raw of list) {
    const code = String(raw || "").toLowerCase().slice(0, 2);
    if (codes.includes(code)) return code;
  }
  return null;
}

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key, fallback) => fallback ?? key,
  enabled: false,
  autoDetected: null,
  clearAutoDetected: () => {},
});

export function I18nProvider({ children }) {
  const [autoDetected, setAutoDetected] = useState(null);

  const [lang, setLangState] = useState(() => {
    if (!ENABLE_I18N || typeof window === "undefined") return DEFAULT_LANG;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LANGUAGES.some((l) => l.code === stored)) return stored;
    const detected = detectBrowserLang();
    return detected || DEFAULT_LANG;
  });

  // First-mount only: if no stored preference and detected lang differs from
  // the German default, expose it via context so the UI can confirm the
  // auto-pick to the visitor (toast / banner). Only fires once per browser.
  useEffect(() => {
    if (!ENABLE_I18N || typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    if (localStorage.getItem(AUTODETECT_KEY)) return;
    const detected = detectBrowserLang();
    if (detected && detected !== DEFAULT_LANG) {
      setAutoDetected(detected);
    }
    localStorage.setItem(AUTODETECT_KEY, "1");
    // We deliberately do NOT depend on `lang` — this is a one-shot signal.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ENABLE_I18N || typeof document === "undefined") return;
    const cfg = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
    document.documentElement.lang = cfg.htmlLang;
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!ENABLE_I18N) return;
    if (!LANGUAGES.some((l) => l.code === code)) return;
    setLangState(code);
  }, []);

  const clearAutoDetected = useCallback(() => setAutoDetected(null), []);

  const t = useCallback(
    (key, fallback) => {
      const dict = translations[lang] || translations[DEFAULT_LANG];
      const value = key
        .split(".")
        .reduce((acc, part) => (acc && acc[part] != null ? acc[part] : null), dict);
      if (value != null) return value;
      // Fallback to DE if missing in selected lang
      const deValue = key
        .split(".")
        .reduce((acc, part) => (acc && acc[part] != null ? acc[part] : null), translations.de);
      return deValue ?? fallback ?? key;
    },
    [lang]
  );

  return (
    <I18nContext.Provider
      value={{ lang, setLang, t, enabled: ENABLE_I18N, autoDetected, clearAutoDetected }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export function useT() {
  return useContext(I18nContext).t;
}
